import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { SashaPageComponent } from "app_sasha/pages/sasha-page.component";
import { ViewsService } from "libraries/view/views.service";
import { AppRootService } from "app/shared/services/app-root/app-root-service";
import { TitleService } from "app_sasha/shared/services/title/title.service";
import { SashaBreadcrumbService } from "app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service";
import {
    IProcessReportSearchFormValue
} from "app_sasha/pages/process-report/components/process-report-search-form/process-report-search-form.component";
import _isEmpty from "lodash-es/isEmpty";
import { QueryParamsMappingService } from "./query-params-mapping.service";
import { ActivatedRoute, Params, Router, UrlSegment } from "@angular/router";
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { filter, mergeMap, tap } from "rxjs/operators";
import { Observable } from "rxjs";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { ProcessReportDomainService } from "app_sasha/pages/process-report/process-report-domain.service";
import { ILoader } from "libraries/component/loader/ILoader";
import { LoaderType } from "libraries/component/loader/loader.component";
import { IPaginationRecordset } from "libraries/component/pagination/IPagination";
import {
    INavigationOutput
} from "app_sasha/pages/process-report/components/process-report-table/process-report-table.component";
import { IProcessReportBookingItem, IProcessReportResponse } from "libraries/interfaces/process-report/i-process-report-response";


export interface IProcessReportSearchParams {
    isEmptySearch?: boolean;
    host?: string[];
    creMin?: string;
    creMax?: string;
    canMin?: string;
    canMax?: string;
    st?: string[];
    pcount?: number;
    pnext?: number;
    pprev?: number;
}

class Wording {
    public readonly loading: string = Yii.t('wui-shared-js', 'Recherche en cours...');
    public readonly exportSuccess: string = Yii.t('wui-shared-js', "L'export a été correctement effectué : ");
    public readonly exportError: string = Yii.t('wui-shared-js', 'Erreur interne');
}

@Component({
    selector: 'process-report-search',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>
            <process-report-search-form [searchRequest]="searchFormValue"
                                        [(isContentVisible)]="isSearchFormVisible"
                                        (onSearch)="navigateToSearch($event, true)"></process-report-search-form>

            <process-report-table [bookingArray]="processReportBookingArray"
                                  [responseRecordSet]="responseRecordSet"
                                  [loader]="loader"
                                  [notification]="notification"
                                  [params]="queryParams"
                                  (onNavigate)="navigateFromPaginationToSearch($event)"></process-report-table>
        </div>
    `
})
export class ProcessReportPageComponent extends SashaPageComponent implements OnInit {

    public wording: Wording = new Wording();

    public isSearchFormVisible: boolean = true;
    public searchFormValue: IProcessReportSearchFormValue;

    public processReportBookingArray: IProcessReportBookingItem[] = [];

    public loader: ILoader = {
        active: false,
        message: this.wording.loading,
        type: LoaderType.large,
    };

    public notification: INotification = {
        active: false,
        message: null,
        type: NotificationType.Info,
    };
    public responseRecordSet: IPaginationRecordset;
    public queryParams: IProcessReportSearchParams;

    constructor(
        protected views: ViewsService,
        readonly appRootService: AppRootService,
        private readonly titleService: TitleService,
        private readonly sashaBreadcrumbService: SashaBreadcrumbService,
        private readonly queryParamsMappingService: QueryParamsMappingService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly processReportDomainService: ProcessReportDomainService,
        private readonly changeDetector: ChangeDetectorRef,
    ) {
        super(views, appRootService);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.titleService.setTitle(Yii.t("wui-sasha-js", "Recherche sur le journal des incidents"));
        this.sashaBreadcrumbService.setProcessReportSearch();
        this.listenToQueryParams();
    }

    public navigateToSearch(formValue: IProcessReportSearchFormValue, newRequest?: boolean): void {
        let queryParams: IProcessReportSearchParams = {isEmptySearch: true};

        if (!_isEmpty(formValue)) {
            queryParams = this.queryParamsMappingService.mapFormToUrl(formValue);
        }
        const targetRoute: UrlSegment[] = this.activatedRoute.snapshot.url;
        this.router.navigate(targetRoute, {queryParams})
            // tslint:disable-next-line:no-any
            .catch((reason: any): void => {
                this.errorTracingService.captureException(new RuntimeError(`Can't navigate to ${targetRoute.join('/')}`, reason));
            });
    }

    public navigateFromPaginationToSearch(paginationOutput: INavigationOutput): void {

        const queryParams: IProcessReportSearchParams = this.queryParamsMappingService.mapPaginationToUrl(paginationOutput, this.queryParams);

        const targetRoute: UrlSegment[] = this.activatedRoute.snapshot.url;

        this.router.navigate(targetRoute, {queryParams})
            // tslint:disable-next-line:no-any
            .catch((reason: any): void => {
                this.errorTracingService.captureException(new RuntimeError(`Can't navigate to ${targetRoute.join('/')}`, reason));
            });
    }

    private listenToQueryParams(): void {
        this.activatedRoute.queryParams.pipe(
            tap((params: Params): void => {
                if (_isEmpty(params)) {
                    this.resetState();
                }
            }),
            filter((params: Params): boolean => !_isEmpty(params)),
            tap((params: IProcessReportSearchParams): void => {
                this.processReportBookingArray = null;
                this.queryParams = params;
                this.searchFormValue = this.queryParamsMappingService.mapUrlToForm(params);
                this.loader.active = true;
                this.notification.active = false;
                this.isSearchFormVisible = false;
                this.changeDetector.markForCheck();
            }),
            mergeMap((params: IProcessReportSearchParams): Observable<IProcessReportResponse> => {
                return this.processReportDomainService.search(params);
            }),
        ).subscribe({
            next: (processReportResponse: IProcessReportResponse): void => {
                this.loader.active = false;
                this.processReportBookingArray = processReportResponse.bookingArray;
                this.responseRecordSet = processReportResponse.recordset;
                if (!this.processReportBookingArray?.length) {
                    this.notification = {
                        active: true,
                        message: Yii.t('wui-shared-js', 'Aucun résultat pour cette recherche'),
                        type: NotificationType.Info,
                    };
                }
            },
            error: (error: Error): void => {
                this.loader.active = false;
                this.notification = {
                    active: true,
                    message: error.message,
                    type: NotificationType.Error,
                };
            }
        });
    }

    private resetState(): void {
        this.searchFormValue = null;
        this.isSearchFormVisible = true;
        this.loader.active = null;
        this.notification.active = null;
        this.processReportBookingArray = null;
    }


}




